import { cn } from "@/lib/utils";
import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useState,
} from "react";

// ============================================================
// Toast — Lightweight notification system
// ============================================================

type ToastType = "success" | "error" | "info";

interface Toast {
	id: string;
	message: string;
	type: ToastType;
}

interface ToastContextValue {
	toast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue>({
	toast: () => {},
});

export function useToast() {
	return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
	const [toasts, setToasts] = useState<Toast[]>([]);

	const toast = useCallback((message: string, type: ToastType = "info") => {
		const id = crypto.randomUUID();
		setToasts((prev) => [...prev, { id, message, type }]);
		setTimeout(() => {
			setToasts((prev) => prev.filter((t) => t.id !== id));
		}, 4000);
	}, []);

	return (
		<ToastContext.Provider value={{ toast }}>
			{children}
			{/* Toast Container */}
			{toasts.length > 0 && (
				<div className="fixed bottom-4 right-4 z-[100] space-y-2">
					{toasts.map((t) => (
						<div
							key={t.id}
							className={cn(
								"rounded-lg px-4 py-3 text-sm shadow-lg animate-slide-up max-w-sm",
								t.type === "success" && "bg-green-600 text-white",
								t.type === "error" && "bg-red-600 text-white",
								t.type === "info" && "bg-stone-800 text-white",
							)}
						>
							{t.message}
						</div>
					))}
				</div>
			)}
		</ToastContext.Provider>
	);
}
